import { useState, useEffect } from "react";
import { Chart } from "@highcharts/react";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../../utils/formatNumber";
import PriceTag from "../../../assets/images/icons/price-tag.png";

import { useAppSelector } from "../../../redux/hooks";

const CurveCharts = ({ optData, vertical = false, price = null }) => {
  const { t } = useTranslation(["pricingv2"]);
  const [demandCurveOptions, setDemandCurveOptions] = useState(null);
  const [profitCurveOptions, setProfitCurveOptions] = useState(null);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);

  const getDemandCurveOptions = (pricingData) => {
    const cats = [];
    const maxDemandValue = null;
    const monthlyDemandValues = {};
    let minPrice = null;
    let maxPrice = null;
    const realDemand = [];
    const spLineDemand = [];
    const priceSeries = [];
    let currentPrice = price;
    let currentPriceY = 0;
    let currentPriceX = 0;

    if (!currentPrice) {
      currentPrice = optData.best_price;
    }

    pricingData.forEach((el) => {
      if (el.num_days_at_price > 0) {
        if (minPrice === null || el.price < minPrice) {
          minPrice = el.price;
        }
        if (maxPrice === null || el.price > maxPrice) {
          maxPrice = el.price;
        }
      }
    });

    pricingData.forEach((el, i) => {
      if (el.modeled_profit) {
        cats.push(
          formatCurrency(el.price, {
            marketPlace: marketPlace,
          }),
        );

        if (el.price === currentPrice) {
          currentPriceY = el.avg_monthly_sales;
          currentPriceX = i;
        } else if (el.price > currentPrice && currentPriceY === 0) {
          currentPriceY = el.avg_monthly_sales;
          currentPriceX = i;
        }

        if (el.num_days_at_price > 0) {
          realDemand.push({
            x: i,
            y: el.avg_monthly_sales,
            z: el.monthly_profit,
            name: formatCurrency(el.price, {
              marketPlace: marketPlace,
            }),
            price: el.price,
          });

          spLineDemand.push({
            x: i,
            y: el.modeled_demand * 30,
            name: formatCurrency(el.price, {
              marketPlace: marketPlace,
            }),
            price: el.price,
          });
        }
      }
    });

    if (currentPriceX > 0 && currentPriceY > 0) {
      priceSeries.push([currentPriceX, currentPriceY]);
    }

    const options = {
      chart: {
        height: 300,
        type: "column",
        zoomType: "x",
        style: {
          fontSize: "14px",
        },
      },
      title: {
        text: null,
      },
      yAxis: [
        {
          title: {
            text: t(($) => $.pricingv2["Monthly Units"]),
          },
          min: 0,
          minimum: 0,
        },
      ],
      xAxis: {
        title: {
          text: t(($) => $.pricingv2["Price"]),
        },
        labels: {
          enabled: true,
        },
        categories: cats,
      },
      legend: {
        enabled: true,
        verticalAlign: "top",
      },
      series: [
        {
          name: t(($) => $.pricingv2["Actual Units Sold"]),
          data: realDemand,
          type: "bubble",
          color: "#c43066",
          opacity: 0.6,
          showInLegend: false,
        },
        {
          name: t(($) => $.pricingv2["Modeled Demand"]),
          data: spLineDemand,
          type: "spline",
          color: "#6e548a",
          opacity: 0.6,
          showInLegend: false,
        },
      ],
      tooltip: {
        formatter: function () {
          try {
            if (monthlyDemandValues[this.y] !== undefined) {
              if (this.y === maxDemandValue) {
                return `${t(($) => $.pricingv2["Recommended Price: {price}"], { price: this.category })}
                <br/><br/>${t(($) => $.pricingv2["Monthly Volume: {volume}"], {
                  volume: (monthlyDemandValues[this.y].modeled_demand * 30).toFixed(0),
                })}`;
              }
              return `${t(($) => $.pricingv2["Price: {price}"], { price: this.category })}<br/><br/>${t(
                ($) => $.pricingv2["Monthly Volume: {volume}"],
                { volume: (monthlyDemandValues[this.y].modeled_demand * 30).toFixed(0) },
              )}`;
            }
            return `${t(($) => $.pricingv2["Price: {price}"], { price: this.category })}<br/><br/>${t(
              ($) => $.pricingv2["Approx. Monthly Sales: {sales}"],
              { sales: this.y.toFixed(0) },
            )}`;
          } catch {
            return "";
          }
        },
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
              maxHeight: 200,
            },
            chartOptions: {
              legend: {
                layout: "horizontal",
                align: "center",
                verticalAlign: "bottom",
              },
            },
          },
        ],
      },
    };

    options.series.push({
      name: t(($) => $.pricingv2["Current Price"]),
      data: priceSeries,
      type: "scatter",
      color: "#6e548a",
      opacity: 1,
      showInLegend: false,
      marker: {
        symbol: `url(${PriceTag})`,
        radius: 5,
        color: "#6e548a",
        opacity: 1,
      },
    });

    return options;
  };

  const getProfitCurveOptions = (pricingData) => {
    const cats = [];
    let minPrice = null;
    let maxPrice = null;
    const priceSeries = [];
    const spLineDemand = [];
    let currentPrice = price;
    let currentPriceY = 0;
    let currentPriceX = 0;

    if (!currentPrice) {
      currentPrice = optData.best_price;
    }

    pricingData.forEach((el) => {
      if (el.num_days_at_price > 0) {
        if (minPrice === null || el.price < minPrice) {
          minPrice = el.price;
        }
        if (maxPrice === null || el.price > maxPrice) {
          maxPrice = el.price;
        }
      }
    });

    const realDemand = [];
    pricingData.forEach((el, i) => {
      if (el.modeled_profit) {
        cats.push(
          formatCurrency(el.price, {
            marketPlace: marketPlace,
          }),
        );
        if (el.num_days_at_price > 0) {
          if (el.price === currentPrice) {
            currentPriceY = el.modeled_profit * 30;
            currentPriceX = i;
          } else if (el.price > currentPrice && currentPriceY === 0) {
            currentPriceY = el.modeled_profit * 30;
            currentPriceX = i;
          }
          realDemand.push({
            x: i,
            y: el.monthly_profit,
            z: el.avg_monthly_sales,
            name: formatCurrency(el.price, {
              marketPlace: marketPlace,
            }),
          });
          spLineDemand.push({
            x: i,
            y: el.modeled_profit * 30,
            name: formatCurrency(el.price, {
              marketPlace: marketPlace,
            }),
          });
        }
      }
    });

    priceSeries.push([currentPriceX, currentPriceY]);

    const options = {
      chart: {
        height: 300,
        type: "column",
        zoomType: "x",
        style: {
          fontSize: "14px",
        },
      },
      title: {
        text: null,
      },
      yAxis: [
        {
          title: {
            text: t(($) => $.pricingv2["Monthly Profit"]),
          },
        },
      ],
      xAxis: {
        title: {
          text: t(($) => $.pricingv2["Price"]),
        },
        labels: {
          enabled: true,
        },
        categories: cats,
      },
      legend: {
        enabled: true,
        verticalAlign: "top",
      },
      series: [
        {
          name: t(($) => $.pricingv2["Actual Margin $"]),
          data: realDemand,
          type: "bubble",
          color: "#c43066",
          opacity: 0.5,
          showInLegend: false,
        },
        {
          name: t(($) => $.pricingv2["Modeled Margin $"]),
          data: spLineDemand,
          type: "spline",
          color: "#6e548a",
          opacity: 0.5,
          showInLegend: false,
        },
      ],
      tooltip: {
        formatter: function () {
          const profit = formatCurrency(this.y, {
            marketPlace: marketPlace,
          });

          return `${t(($) => $.pricingv2["Price: {price}"], { price: this.category })}<br/><br/>${t(
            ($) => $.pricingv2["Monthly Profit: {profit}"],
            { profit },
          )}`;
        },
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
              maxHeight: 200,
            },
            chartOptions: {
              legend: {
                layout: "horizontal",
                align: "center",
                verticalAlign: "bottom",
              },
            },
          },
        ],
      },
    };

    options.series.push({
      name: t(($) => $.pricingv2["Current Price"]),
      data: priceSeries,
      type: "scatter",
      color: "#6e548a",
      opacity: 1,
      showInLegend: false,
      marker: {
        symbol: `url(${PriceTag})`,
        radius: 5,
        color: "#6e548a",
        opacity: 1,
      },
    });

    return options;
  };

  useEffect(() => {
    if (!optData?.order_history || !optData.pricing_data) {
      return null;
    }

    const pricingData = optData.pricing_data;

    setDemandCurveOptions(getDemandCurveOptions(pricingData));
    setProfitCurveOptions(getProfitCurveOptions(pricingData));

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [optData, price]);

  if (!optData?.order_history || !optData.pricing_data) {
    return null;
  }

  return (
    <Row className={`pt-4 pb-4`}>
      <Col xs={12} md={12} lg={vertical ? 12 : 6}>
        <h6 className={`text-center`}>{t(($) => $.pricingv2["Demand Elasticity"])}</h6>
        <Chart options={demandCurveOptions} />
      </Col>
      <Col xs={12} md={12} lg={vertical ? 12 : 6} className={vertical ? "pt-4" : ""}>
        <h6 className={`text-center`}>{t(($) => $.pricingv2["Profit Sensitivity"])}</h6>
        <Chart options={profitCurveOptions} />
      </Col>
    </Row>
  );
};

export default CurveCharts;
